import React, { useEffect, useRef, useState } from 'react'
import { useBreakpoint } from 'gatsby-plugin-breakpoints'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import SplitType from 'split-type'

import LayoutSEO from 'components/SEM/LayoutSEM'
import SEO from 'components/SEO'
import Line from 'components/Line'
import LinkButton from 'components/LinkButton'
import Founder from 'components/AboutFounder'
import Recognition from 'components/AboutRecognition'
import CultureListItem from 'components/AboutCultureListItem'

import { SERVICES_ROUTE } from 'routes/constants'

// Images
import HeroOne from 'images/about/hero-1.webp'
import HeroTwo from 'images/about/hero-2.webp'
import HeroThree from 'images/about/hero-3.webp'
import HeroFour from 'images/about/hero-4.webp'
import HeroFive from 'images/about/hero-5.avif'
import HeroSix from 'images/about/hero-6.webp'
import HeroSeven from 'images/about/hero-7.webp'
import HeroEight from 'images/about/hero-8.webp'
import HeroNine from 'images/about/hero-9.webp'
import HeroTen from 'images/about/hero-10.webp'
import HeroEleven from 'images/about/hero-11.webp'
import JoeDesktop from 'images/about/joe-desktop.webp'
import MollyDesktop from 'images/about/molly-desktop.webp'
import RobDesktop from 'images/about/rob-desktop.webp'
import ThibaultDesktop from 'images/about/thibault-desktop.webp'
import CultureMobile from 'images/about/culture-mobile.webp'
import CultureDesktop from 'images/about/culture-desktop.webp'

import TodayIcon from 'images/about/today.svg'
import NYTIcon from 'images/about/nyt.svg'
import ForbesIcon from 'images/about/forbes.svg'
import VentureBeatIcon from 'images/about/venturebeat.svg'

gsap.registerPlugin(ScrollTrigger)

const AboutPage = () => {
  const locationTitleRef = useRef(null)
  const newSplitTitleRef = useRef(null)
  const cultureImageRef = useRef(null)
  const breakpoints = useBreakpoint()
  const [isSplit, setIsSplit] = useState(false)
  const [isTitleInView, setTitleIsInView] = useState(false)
  const activeImageIndex = [0, 1, 2]

  useEffect(() => {
    setTimeout(() => {
      if (window && document && locationTitleRef.current) {
        // Split text
        const locationTitlelines = new SplitType(locationTitleRef.current, {
          types: 'lines, words',
        })
        newSplitTitleRef.current = locationTitlelines.words

        locationTitlelines.words.map((word) => {
          gsap.set(word, { opacity: 1, yPercent: 100 })
          return word
        })

        setIsSplit(true)

        // Location text animation
        gsap.timeline({
          scrollTrigger: {
            trigger: locationTitleRef.current,
            start: 'top 80%',
            end: 'bottom 20%',
            onToggle: ({ isActive }) => {
              setTitleIsInView(isActive)
            },
          },
        })

        // Culture image animation
        gsap.set(cultureImageRef.current, {
          opacity: 0,
          xPercent: -2.5,
        })
        gsap.timeline({
          scrollTrigger: {
            trigger: cultureImageRef.current,
            start: 'top 95%',
            end: 'bottom 5%',
            onEnter: () => {
              gsap.to(cultureImageRef.current, {
                opacity: 1,
                xPercent: 0,
              })
            },
            onLeaveBack: () => {
              gsap.to(cultureImageRef.current, {
                opacity: 0,
                xPercent: -2.5,
              })
            },
            onLeave: () => {
              gsap.to(cultureImageRef.current, {
                opacity: 0,
                xPercent: -2.5,
              })
            },
            onEnterBack: () => {
              gsap.to(cultureImageRef.current, {
                opacity: 1,
                xPercent: 0,
              })
            },
          },
        })

        // Intro images animations
        setTimeout(() => {
          const introImages = document.querySelectorAll('.intro-images img')
          introImages.forEach((image, index) => {
            const endPosition = image.offsetLeft + image.offsetWidth
            if (index <= 2) {
              const duration = getDuration(index, true)
              gsap.to(image, {
                x: -endPosition,
                duration,
                ease: 'none',
                onUpdate: () => {
                  if (index === 2) {
                    onImageUpdate(index + 1)
                  }
                },
                onComplete: () => onComplete(index, image),
              })
            }
          })
        }, 1000)
      }
    })
  }, [])

  useEffect(() => {
    if (!isSplit) return
    if (isTitleInView) {
      newSplitTitleRef.current.forEach((word, index) => {
        gsap.to(word, {
          opacity: 1,
          yPercent: 0,
          delay: index > 2 ? 0.25 : 0,
        })
      })
    } else {
      newSplitTitleRef.current.forEach((word) => {
        gsap.to(word, { opacity: 1, yPercent: 100 })
      })
    }
  }, [isSplit, isTitleInView, locationTitleRef.current])

  function onImageUpdate(index) {
    // on update if image is animating and is fully in view start the next image
    if (!activeImageIndex.includes(index) && index < 11) {
      const nextIndex = index === 0 ? 11 : index
      const nextImage = document.querySelectorAll('.intro-images img')[index]
      const prevImage =
        document.querySelectorAll('.intro-images img')[nextIndex - 1]
      if (!nextImage || !prevImage) return
      const prevImageRect = prevImage.getBoundingClientRect()
      if (prevImageRect.right < window.innerWidth) {
        activeImageIndex.push(index)
        const endPosition = nextImage.offsetLeft + nextImage.offsetWidth
        const duration = getDuration(index)
        gsap.to(nextImage, {
          x: -endPosition,
          duration,
          ease: 'none',
          onUpdate: () => {
            if (index < 10) {
              onImageUpdate(index + 1)
            } else {
              setTimeout(() => {
                onImageUpdate(0)
              }, 4000)
            }
          },
          onComplete: () => onComplete(index, nextImage),
        })
      }
    }
  }

  function onComplete(index, image) {
    // remove from activeImageIndex & reset position
    activeImageIndex.splice(activeImageIndex.indexOf(index), 1)
    gsap.set(image, {
      x: 0,
      left: '100%',
    })
  }

  function getDuration(index, isFirstRun = false) {
    // first 3 indexes are quicker than the rest to begin with
    if (index === 0 && isFirstRun) {
      return 28
    }

    if (index === 1 && isFirstRun) {
      return 54
    }

    if (index === 2 && isFirstRun) {
      return 60
    }

    // First 3 reset to same as above after first run
    return Math.random() * (100 - 80) + 80
  }

  return (
    <LayoutSEO theme="studio" isAboutPage>
      <SEO
        title="About | Studio"
        description="Solving problems and crafting solutions that make your business grow. We make digital products, not just software."
      >
        <link rel="canonical" href="https://buildwithstudio.com/about/" />
      </SEO>
      <div className="about__hero">
        <header className="page-header">
          <h1 className="page-heading">
            About Studio
            <Line />
          </h1>
          <p className="page-subheading">A place where creative minds work</p>
        </header>
        <div className="about-page__intro">
          <div className="about-page__intro-content">
            <div className="intro-images">
              <img className="intro-size-1" src={HeroOne} alt="Studio" />
              <img className="intro-size-2" src={HeroTwo} alt="Studio" />
              <img className="intro-size-3" src={HeroThree} alt="Studio" />
              <img className="intro-size-2" src={HeroFour} alt="Studio" />
              <img className="intro-size-3" src={HeroFive} alt="Studio" />
              <img className="intro-size-3" src={HeroSix} alt="Studio" />
              <img className="intro-size-1" src={HeroSeven} alt="Studio" />
              <img className="intro-size-3" src={HeroEight} alt="Studio" />
              <img className="intro-size-1" src={HeroNine} alt="Studio" />
              <img className="intro-size-2" src={HeroTen} alt="Studio" />
              <img className="intro-size-1" src={HeroEleven} alt="Studio" />
            </div>
          </div>
        </div>
      </div>
      <div className="about-page__content">
        <div className="about-page__locations">
          <p>Working from</p>
          <h2 ref={locationTitleRef}>
            New York, London,
            <br />
            Miami, Bordeaux.
          </h2>
          <ul>
            <li>
              <p>26</p>
              <p>Global Team Members</p>
            </li>
            <li>
              <p>10</p>
              <p>Years building together</p>
            </li>
            <li>
              <p>100+</p>
              <p>Projects Launched</p>
            </li>
          </ul>
        </div>
        <div className="about-page__founders">
          <h2 className="about-section__title">Teams Led by Founders</h2>
          <p>
            At Studio, our boutique size enables us to have founding team
            members and directors oversee every project. These leaders have
            built, scaled, and launched companies of their own and are experts
            in their discipline.
          </p>
        </div>
        <div className="founders">
          <Founder
            name="Joe"
            title="Founder & Product Director"
            image={JoeDesktop}
          />
          <Founder
            name="Molly"
            title="Accounts Director"
            image={MollyDesktop}
          />
          <Founder name="Rob" title="Creative Director" image={RobDesktop} />
          <Founder
            name="Thibault"
            title="iOS Director"
            image={ThibaultDesktop}
          />
        </div>
        <div className="about-page__values --cream">
          <h2 className="about-section__title --has-line">
            What Makes Us Tick
            <Line hasButtonSibling />
            {!breakpoints.m && (
              <LinkButton route={SERVICES_ROUTE} text="Services and process" />
            )}
          </h2>
          <p>
            Studio exists to support founders at every stage. We solve problems
            with technology to make the world a more efficient place, improving
            quality of life and disrupting antiquated industries.
          </p>
          <div className="values-grid">
            <p>
              We are builders and executers, not agency folk. Many of us have
              founded startups and understand intimately what it takes to find
              product market fit.{' '}
            </p>
            <p>
              We’ve raised money, we’ve led teams, we’ve designed, we’ve built,
              we’ve exited.{' '}
            </p>
            <p>
              We’re hyper transparent and focus on what the product truly needs
              for success - approaching every product with a disciplined
              methodology; Execute quickly, get to market, gather feedback,
              iterate.
            </p>
            <p>
              The other arm of Studio is our labs division. When our team
              members see a gap in the market, we give them the flexibility to
              explore these opportunities using that same tried and true
              methodology.
            </p>
            <p>
              Labs is a safe space for creators to play and build toward the
              never ending pursuit of traction.
            </p>
            <p>
              Learn more about our recent labs projects:{' '}
              <a target="_blank" href="https://refer.shop/" rel="noreferrer">
                Refer
              </a>
              ,{' '}
              <a target="_blank" href="https://sellwith.live/" rel="noreferrer">
                LIVE
              </a>
              , and{' '}
              <a
                href="https://encouraging-chip-fbc.notion.site/Checkout-Upsell-by-Studio-120966b89f7b4da3afd794e657705307"
                target="_blank"
                rel="noreferrer"
              >
                Checkout
              </a>
              .
            </p>
          </div>
          <p>
            We help enterprise industry leaders stay on the forefront of the
            cutting edge and revive those who lag behind.
          </p>
          {breakpoints.m && (
            <LinkButton route={SERVICES_ROUTE} text="Services and process" />
          )}
        </div>
        <div className="about-page__culture">
          <div className="about-page__culture-container">
            <h2 className="about-section__title">Culture</h2>
          </div>
          <div className="culture-grid">
            <picture ref={cultureImageRef}>
              <source srcSet={CultureMobile} media="(max-width: 768px)" />
              <source srcSet={CultureDesktop} media="(min-width: 769px)" />
              <img src={CultureMobile} alt="Culture" />
            </picture>
            <div className="culture-content">
              <p>
                Many of our team members have worked together for a decade,
                building an incredible foundation of trust, collaboration, and
                honesty. In daily stand ups and working sessions, all
                perspectives are welcome and teams are encouraged to think
                creatively and expansively.
              </p>
              <p>All of our roles enjoy:</p>
              <ul>
                <CultureListItem title="Flexibility in remote work" />
                <li>
                  <Line />
                </li>
                <CultureListItem title="Unlimited time off" />
                <li>
                  <Line />
                </li>
                <CultureListItem title="Zero time tracking" />
                <li>
                  <Line />
                </li>
                <CultureListItem title="Company team travel" />
                <li>
                  <Line />
                </li>
                <CultureListItem title="Learning & growth opportunities" />
                <li>
                  <Line />
                </li>
                <CultureListItem title="Parental leave" />
                <li>
                  <Line />
                </li>
                <CultureListItem title="Healthcare benefits" />
                <li>
                  <Line />
                </li>
                <CultureListItem title="Yearly bonus" />
                <li>
                  <Line />
                </li>
              </ul>
            </div>
          </div>
        </div>
        <div className="about-page__careers">
          <h2 className="about-section__title">Careers</h2>
          <div className="careers-grid">
            <div className="careers-content">
              <p>
                We are always looking for incredible talent. If you are
                interested in being part of our team, please reach out to{' '}
                <a href="mailto:hello@buildwithstudio.com">
                  hello@buildwithstudio.com
                </a>{' '}
                with your resume and a bit about who you are!
              </p>
            </div>
            {/* <blockquote className="career">
              <img src={BlockquoteImage} alt="Marisa Jensen Designer" />
              <h3>
                Studio is where I really glowed up, as a designer and as a
                person.
              </h3>
              <p>Marisa Jensen</p>
              <p>Designer</p>
            </blockquote> */}
          </div>
        </div>
        <div className="about-page__recognition">
          <div className="recognition-grid">
            <h2 className="about-section__title">Recognition</h2>
            <Recognition
              route="https://venturebeat.com/media/creatively-launches-job-platform-for-creative-professionals/"
              title="Creatively launches job platform for creative professionals"
              text="“Creatively focuses on enabling creators to show off their portfolios, with high-resolution images and 30-second videos. The platform also has collaborative features and was designed by creatives for creatives.”"
              logo={VentureBeatIcon}
            />
            <Recognition
              route="https://www.nytimes.com/2022/05/09/sports/baseball/tom-house-mustard.html"
              title="King of Throws"
              text="“Tom House rebuilt Nolan Ryan and fixed Randy Johnson. He worked with Tom Brady and Drew Brees. At 75, he has reinvented himself (again) with an app that teaches young players to pitch the right way.”"
              logo={NYTIcon}
            />
            <Recognition
              route="https://www.forbes.com/sites/alexandrasternlicht/2019/10/02/instagram-influencer-danielle-bernstein-weworewhat-launches-tech-suite-for-influencers/?sh=133c6b424839"
              title="Instagram Influencer Danielle Bernstein (WeWoreWhat) Launches Tech Suite For Influencers"
              text="“The 27-year-old style influencer, known to her 2.2 million Instagram followers as WeWoreWhat, has built a multimillion-dollar business from being best-dressed and posting her outfits to Instagram. Today, she’s adding tech founder to her résumé, launching Moe Assist, a tech suite for influencers to run their business.”"
              logo={ForbesIcon}
            />
            <Recognition
              route="https://www.cbsnews.com/chicago/news/ryan-mundy-alkeme-app-black-mental-health/"
              title="Former NFL player tackles mental health in Black community"
              text="“NFL veteran Ryan Mundy opens up about the physical and mental toll he faced playing football and the how that led him to create a mental health platform called Alkeme.”"
              logo={TodayIcon}
            />
          </div>
        </div>
      </div>
    </LayoutSEO>
  )
}

export default AboutPage
